import { Box, CircularProgress, Grid, Paper, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts";
import dayjs from "dayjs";
import { TimeNumberProps } from "./_type";
import { COLOR } from "../../../utils/colors";
import { RobotTaskType } from "../../../hooks/robotTask/index.type";
import { chartLabel } from "../../../utils/styles";
import { countTotalDays } from "../../../utils/utils";

type RobotDurationType = {
  id: string,
  name: string,
  startTime: number[],
  durations: string[],
  totalMinutes: number,
  totalDurationInSeconds: number
}

export default function DPRate({
  startTime,
  endTime,
  data,
  isLoading
}: TimeNumberProps & { data: RobotTaskType[], isLoading: boolean }) {
  const time = dayjs.unix(endTime).diff(dayjs.unix(startTime), 'days')
  const startHour = '08:30:00'
  const endHour = '20:30:00'

  // Calculate diff startHour and endHour in seconds
  const startHourInSeconds = dayjs(startHour, 'HH:mm:ss').diff(dayjs('00:00:00', 'HH:mm:ss'), 'seconds');
  const endHourInSeconds = dayjs(endHour, 'HH:mm:ss').diff(dayjs('00:00:00', 'HH:mm:ss'), 'seconds');
  // Calculate total time in seconds
  const totalTimeInSeconds = endHourInSeconds - startHourInSeconds;

  const weekendsIndexs = [6, 0]; // Saturday and Sunday
  const filteredData = (data || []).filter((i) => {
    const isCorrectLocation = i.from_location.includes('DP') || (i.route?.[0] === 'DP')
    const currentStartTime = dayjs.unix(i.start_time)
    const currentEndTime = dayjs.unix(i.end_time)
    const currentDate = currentStartTime.format('YYYY-MM-DD');
    // Check if the start time is after 08:30 and end time is before 20:30
    const isValidStartHour = currentStartTime.isAfter(dayjs(currentDate + ' ' + startHour, 'YYYY-MM-DD HH:mm:ss'))
    const isValidEndHour = currentEndTime.isBefore(dayjs(currentDate + ' ' + endHour, 'YYYY-MM-DD HH:mm:ss'))

    const isValidDay = !weekendsIndexs.includes(currentStartTime.day());
    return isCorrectLocation && isValidStartHour && isValidEndHour && isValidDay
  })

  const totalDurationsTMP = filteredData.filter((i) => i && i.duration && i.duration.toString() !== '-1').map((item) => {
    const duration = item.duration.toString();

    const minutes = parseInt(duration.split(':')[0])
    const seconds = parseInt(duration.split(':')[1]) / 60

    const totalMinutes = minutes + seconds

    const totalSeconds = parseInt(duration.split(':')[2]) || 0
    const totalDurationInSeconds = (totalMinutes * 60) + totalSeconds

    return {
      robot: {
        id: item.robot_id,
        name: item.robot_name,
        startTime: [item.start_time],
        durations: [duration]
      },
      totalMinutes,
      totalDurationInSeconds
    }
  })

  const totalDurationGroupedByRobot = totalDurationsTMP.reduce((acc: { [key: string]: RobotDurationType }, item) => {
    const robotId = item.robot.id;
    if (!acc[robotId]) {
      acc[robotId] = { ...item.robot, totalMinutes: 0, totalDurationInSeconds: 0, durations: [], startTime: [] };
    }
    acc[robotId].startTime.push(...item.robot.startTime);
    acc[robotId].durations.push(...item.robot.durations.map((d) => d.toString()));
    acc[robotId].totalMinutes += item.totalMinutes;
    acc[robotId].totalDurationInSeconds += item.totalDurationInSeconds;
    return acc;
  }, {} as { [key: string]: RobotDurationType })

  const dataGroupedByRobot = Object.values(totalDurationGroupedByRobot).map((item) => ({
    ...item,
    totalMinutes: parseFloat(item.totalMinutes.toFixed(2))
  })).sort((a, b) => b.totalMinutes - a.totalMinutes)

  const totalDuration = totalDurationsTMP.reduce((a, b) => a + b.totalDurationInSeconds, 0)
  const totalCapacity = (totalTimeInSeconds) * (countTotalDays(startTime, endTime, weekendsIndexs) || 1); // Total capacity in seconds (HOURS_IN_DAY * time in days * 3600 seconds/hour)
  const busyTime = totalDuration
  const idleTime = totalCapacity - totalDuration

  const total = busyTime + idleTime
  const calcPercentage = (value: number) => parseFloat(((value / total) * 100).toFixed(2))

  const renderSecondsToTimeLabel = (value: number) => {
    // Render seconds to HH:mm:ss format and round to 2 decimal places
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    const seconds = value % 60;
    if (hours === 0) {
      return `${minutes.toString().padStart(2, '0')} min ${seconds.toString().padStart(2, '0')} sec`;
    }
    return `${hours.toString().padStart(2, '0')} hour ${minutes.toString().padStart(2, '0')} min ${seconds.toString().padStart(2, '0')} sec`;  
  }

  return (
    <Grid item xs={12} md={6}>
      <Paper
        elevation={0}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          cursor: "pointer",
          width: "100%",
          flex: "auto",
          border: `1px solid ${COLOR.gray[400]}`
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            my: '12px',
            ml: '14px',
            mr: '24px',
            gap: '6px'
          }}
        >
          <Typography className='font-poppins' variant="h6" sx={{ color: COLOR.black, fontSize: '16px' }}>
            DP Utilisation Rate
          </Typography>
          <Typography variant="h6" sx={{ color: "#9E9E9E", fontSize: '12px', mt: '-4px' }}>
            {time === 1 ? 'Yesterday' : time === 0 ? 'Today' : dayjs.unix(startTime).format('DD/MM/YY') + ' - ' + dayjs.unix(endTime).format('DD/MM/YY')}
          </Typography>
          <Typography variant="h6" sx={{ color: "#9E9E9E", opacity: 0.8, fontSize: '12px', fontWeight: 400, mt: '-4px' }}>
            Utilisation rate is captured from 8.30am – 8.30pm, Mon – Fri
          </Typography>
          {isLoading && (
            <CircularProgress />
          )}
          {data.length > 0 && (
            <PieChart
              skipAnimation
              series={[
                {
                  innerRadius: 45,
                  outerRadius: 80,
                  arcLabel: (item) => item.value > 0 ? `${calcPercentage(item.value)}%` : '',
                  // data: [
                  //   { id: 0, value: busyTime, label: 'Busy', color: COLOR.graph.orange },
                  //   { id: 1, value: idleTime, label: 'Idle', color: COLOR.graph.green },
                  //   // { id: 2, value: error, label: 'Error', color: '#fd3636' },
                  // ],
                  valueFormatter: ({ value }: { value: number }) => renderSecondsToTimeLabel(value),
                  data: [
                    ...dataGroupedByRobot.map((item, index) => ({
                      id: index,
                      value: item.totalDurationInSeconds,
                      label: `Busy - ${item.name}`,
                    })),
                    { id: dataGroupedByRobot.length + 1, value: idleTime, label: 'Idle', color: COLOR.graph.green },
                  ]
                },
              ]}
              sx={{ ...chartLabel }}
              height={200}
            />
          )}
          {!isLoading && data.length <= 0 && (
            <Box sx={{ width: '100%', height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h6" sx={{ fontSize: '12px' }}>
                No Data to Display
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Grid>
  )
}